import React from 'react';
import { Input } from '../../ui/input';
import { User, Mail, Phone, MapPin, Globe, Linkedin, Camera, Loader2 } from 'lucide-react';

export const Step2BasicInfo = ({ formData, setFormData, fileInputRef, uploadingPhoto, handlePhotoUpload }) => {
  const isLogo = formData.profile_type === 'company' || formData.profile_type === 'institution';
  const photoLabel = isLogo ? 'Logomarca' : 'Foto do Perfil';
  return (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h2 className="text-2xl font-bold text-white mb-2">Informações Básicas</h2>
      <p className="text-gray-400">Como as pessoas vão te encontrar</p>
    </div>

    {/* Photo Upload Section */}
    <div className="flex justify-center mb-6">
      <div className="relative">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-3xl font-bold overflow-hidden">
          {formData.profile_photo ? (
            <img
              src={formData.profile_photo}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          ) : (
            formData.name?.charAt(0)?.toUpperCase() || <User className="w-10 h-10" />
          )}
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingPhoto}
          className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
          data-testid="upload-photo-btn"
        >
          {uploadingPhoto ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Camera className="w-4 h-4" />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handlePhotoUpload}
          className="hidden"
        />
      </div>
    </div>
    <p className="text-center text-sm text-gray-300 -mt-4 mb-1 font-medium">{photoLabel}</p>
    <p className="text-center text-xs text-gray-500 mb-4 max-w-md mx-auto">
      Clique no ícone para adicionar. Tipos aceitos: JPG, PNG e WEBP; Tamanho máximo: 5 MB; Dimensões recomendadas: mínimo 400×400px.
      {isLogo && ' Para logomarca, recomendamos PNG com fundo transparente.'}
    </p>

    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm text-gray-400 mb-2">Nome completo *</label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Seu nome"
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
            data-testid="profile-name-input"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Email *</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="seu@email.com"
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
            data-testid="profile-email-input"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Telefone</label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="(11) 99999-9999"
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">WhatsApp</label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={formData.whatsapp}
            onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
            placeholder="(11) 99999-9999"
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm text-gray-400 mb-2">Título profissional</label>
        <Input
          value={formData.headline}
          onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
          placeholder="Ex: BIM Manager | Coordenador de Projetos | Estudante de Arquitetura"
          className="bg-zinc-800 border-zinc-700 text-white"
          data-testid="profile-headline-input"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Localização</label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            placeholder="Cidade, Estado"
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Website</label>
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={formData.website}
            onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
            placeholder="https://seusite.com"
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm text-gray-400 mb-2">LinkedIn</label>
        <div className="relative">
          <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={formData.linkedin}
            onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
            placeholder="https://linkedin.com/in/seuperfil"
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm text-gray-400 mb-2">Sobre você</label>
        <textarea
          value={formData.bio}
          onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
          placeholder="Conte um pouco sobre sua experiência e objetivos..."
          rows={4}
          className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
          data-testid="profile-bio-input"
        />
      </div>
    </div>
  </div>
  );
};

export default Step2BasicInfo;
